//frontend\src\pages\LegalAdvisorDetails.js

import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/LegalAdvisorDetails.css';
import Chat from '../components/Chat';
import BookingModal from '../components/consultation/BookingModal';
import { BsChatDots, BsCameraVideo } from 'react-icons/bs';
import { IoMdClose } from 'react-icons/io';
import '../styles/consultation.css';

const LegalAdvisorDetails = () => {
  const { id } = useParams();
  const [advisor, setAdvisor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [advisorMessageCount, setAdvisorMessageCount] = useState(0);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [showVideoConsult, setShowVideoConsult] = useState(false);
  const navigate = useNavigate();
  const chatRefreshInterval = useRef(null);

  // Function to fetch advisor message count
  const fetchAdvisorMessageCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !currentUserId || !id) return;

      const response = await fetch(`/api/auth/chat/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.messages) {
          const advisorMessages = data.messages.filter(msg => 
            msg.sender._id === id && 
            msg.sender._id !== currentUserId && 
            !msg.deletedFor?.includes(currentUserId) && 
            !msg.deletedForEveryone
          );
          setAdvisorMessageCount(advisorMessages.length);
        }
      }
    } catch (error) {
      console.error('Error fetching advisor message count:', error);
    }
  };

  // Function to fetch unread message count
  const fetchUnreadMessageCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !currentUserId || !id) return;

      // Fetch chat messages between current user and advisor
      const response = await fetch(`/api/auth/chat/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.messages) {
          // Count unread messages from advisor to current user
          const unreadMessages = data.messages.filter(msg => 
            msg.sender._id === id && // Message from advisor
            msg.sender._id !== currentUserId && // Not from current user
            !msg.deletedFor?.includes(currentUserId) && // Not deleted for current user
            !msg.deletedForEveryone && // Not deleted for everyone
            !msg.readBy?.includes(currentUserId) // Not read by current user
          );
          setUnreadMessageCount(unreadMessages.length);
        }
      }
    } catch (error) {
      console.error('Error fetching unread message count:', error);
    }
  };

  useEffect(() => {
    const fetchAdvisorDetails = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        // Fetch current user's ID and role
        const userResponse = await fetch('/api/auth/profile', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const userData = await userResponse.json();
        setCurrentUserId(userData.user._id);
        setUserRole(userData.user.role);

        // Fetch advisor details
        const response = await fetch(`/api/auth/users/${id}`);
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Legal Advisor not found');
          }
          if (response.status === 403) {
            throw new Error('User is not a legal advisor');
          }
          throw new Error('Failed to fetch legal advisor details');
        }
        const data = await response.json();
        setAdvisor(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
  
    fetchAdvisorDetails();
  }, [id, navigate]);

  // Effect to fetch unread message count and set up polling
  useEffect(() => {
    if (currentUserId && id) {
      fetchUnreadMessageCount();
      
      // Set up interval to refresh unread count every 5 seconds
      chatRefreshInterval.current = setInterval(fetchUnreadMessageCount, 5000);
      
      return () => {
        if (chatRefreshInterval.current) {
          clearInterval(chatRefreshInterval.current);
        }
      };
    }
  }, [currentUserId, id]);

  // Effect to clear unread count when chat is opened
  useEffect(() => {
    if (showChat) {
      setUnreadMessageCount(0);
    }
  }, [showChat]);

  const handleChatClick = () => {
    if (!localStorage.getItem('token')) {
      navigate('/login');
      return;
    }
    setShowChat(!showChat);
    // Clear unread count when chat is opened
    if (!showChat) {
      setUnreadMessageCount(0);
    }
  };

  const handleVideoConsultClick = () => {
    if (!localStorage.getItem('token')) {
      navigate('/login');
      return;
    }
    setShowVideoConsult(true);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  // Check if the current user is viewing their own profile
  const isViewingOwnProfile = currentUserId === id;

  // --- CHANGES: Move main container and profile card to the top for visual priority ---
  return (
    <div className="advisor-details-main-container">
      {/* Left Profile Card */}
      <aside className="advisor-profile-card">
        <div className="advisor-photo-circle">
          <img
            src={advisor.profilePhoto || '/default-profile.png'}
            alt={`${advisor.firstName} ${advisor.lastName}`}
            className="advisor-photo-img"
            onError={e => { e.target.src = '/default-profile.png'; }}
          />
        </div>
        <div className="advisor-profile-info">
          <h2 className="advisor-name-main notranslate" translate="no">
            Adv. {advisor.firstName} {advisor.lastName}
          </h2>
          <div className="advisor-specialization">{advisor.specialization}</div>
          <div className="advisor-experience">
            <strong>Experience:</strong> {advisor.experience} Years
          </div>
          <div className="advisor-languages">
            <strong>Languages:</strong> {advisor.languagesKnown?.join(', ') || 'N/A'}
          </div>
          <div className="advisor-location">
            <strong>Location:</strong> {advisor.district}, {advisor.state}
          </div>
          {!isViewingOwnProfile && (
            <div className="advisor-action-buttons">
              <button
                className="advisor-contact-btn"
                onClick={handleChatClick}
              >
                <BsChatDots style={{ fontSize: 20 }} />
                {showChat ? "Close Chat" : "Chat Now"}
              </button>
              <button
                className="advisor-contact-btn"
                onClick={handleVideoConsultClick}
              >
                <BsCameraVideo style={{ fontSize: 20 }} />
                Video Consult
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Right Main Content */}
      <main className="advisor-main-content">
        {/* About Section */}
        <section className="advisor-section">
          <h3 className="advisor-section-title">About Lawyer <span className="notranslate" translate="no">{advisor.firstName} {advisor.lastName}</span></h3>
          <p>{advisor.about || "No about info provided."}</p>
          <div className="advisor-about-details">
            <div>
              <strong>Experience:</strong> {advisor.experience} Years
            </div>
            <div>
              <strong>Enrollment Number:</strong> {advisor.enrollmentNumber || 'N/A'}
            </div>
            <div>
              <strong>Address:</strong> {advisor.address || 'N/A'}
            </div>
            <div>
              <strong>Primary Practice Area:</strong> {advisor.primaryPracticeAreas?.[0] || 'N/A'}
            </div>
          </div>
          <div className="advisor-languages-tags">
            {advisor.languagesKnown?.map(lang => (
              <span className="advisor-tag" key={lang}>{lang}</span>
            ))}
          </div>
        </section>

        {/* Work in Courts Section */}
        <section className="advisor-section">
          <h3 className="advisor-section-title">Work in Courts</h3>
          <div className="advisor-courts-tags">
            {advisor.primaryPracticeAreas?.map(court => (
              <span className="advisor-tag" key={court}>{court}</span>
            ))}
          </div>
        </section>
      </main>

      {/* Only show chat button if not viewing own profile */}
      {!isViewingOwnProfile && (
        <div className="chat-button-container">
          {/* Unread message count badge */}
          {unreadMessageCount > 0 && !showChat && (
            <div className="advisor-message-badge">
              {unreadMessageCount}
            </div>
          )}
          <button className="chat-icon-button" onClick={handleChatClick} title="Chat with Advisor">
            {showChat ? <IoMdClose size={24} /> : <BsChatDots size={24} />}
          </button>
        </div>
      )}

      {/* Chat Popup */}
      {showVideoConsult && !isViewingOwnProfile && (
        <BookingModal
          advisor={advisor}
          onClose={() => setShowVideoConsult(false)}
        />
      )}

      {showChat && currentUserId && !isViewingOwnProfile && (
        <div className="chat-popup">
          <div className="chat-popup-header">
            <h3>Chat with <span className="notranslate" translate="no">Adv.{advisor.firstName} {advisor.lastName}</span></h3>
            <button className="close-chat-button" onClick={() => setShowChat(false)}>
              <IoMdClose size={20} />
            </button>
          </div>
          <div className="chat-popup-content">
            <Chat advisorId={id} userId={currentUserId} />
          </div>
        </div>
      )}
    </div>
  );
};

export default LegalAdvisorDetails;
